import { MaintenancePlanGroupWithCodes } from "@boark/shared/types/maintenance-plan-group-with-codes";
import { Outlet, Route, Routes, useSearchParams } from "react-router-dom";

import { Button } from "../../components/button";
import { H1 } from "../../components/h1";
import { Column, Filter, Table } from "../../components/table";
import { useGetMaintenancePlanGroups } from "../../queries/maintenance-plan-group";
import { useGetMaintenancePlans } from "../../queries/maintenance-plan";
import { useLocale } from "../../stores/locale";
import { recordIdToId } from "../../utils/record-id-to-id";
import { MaintenancePlanGroupCreate } from "./maintenance-plan-group-create";
import { MaintenancePlanGroupDelete } from "./maintenance-plan-group-delete";
import { MaintenancePlanGroupEdit } from "./maintenance-plan-group-edit";

export function MaintenancePlanGroupList() {
  const { locale: t } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: groups, isLoading: isGroupsLoading } =
    useGetMaintenancePlanGroups();
  const { data: maintenancePlans } = useGetMaintenancePlans();

  const usedGroupIds = new Set(
    maintenancePlans?.map((plan) =>
      recordIdToId(plan.maintenancePlanCode.maintenancePlanGroup.id),
    ) ?? [],
  );

  function updateSearchParams(params: Record<string, string>) {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      ...params,
    });
  }

  return (
    <>
      <Routes>
        <Route element={<MaintenancePlanGroupCreate />} path="/create" />
        <Route element={<MaintenancePlanGroupEdit />} path="/:id/edit" />
        <Route element={<MaintenancePlanGroupDelete />} path="/:id/delete" />
      </Routes>
      <Outlet />
      <div className="w-full min-h-full overflow-x-auto">
        <div className="flex justify-between items-center">
          <H1>{t.maintenance.groupsTab}</H1>
          <Button route="/admin/maintenance/groups/create">
            {t.maintenance.createGroup}
          </Button>
        </div>
        <Table
          data={groups || []}
          loading={isGroupsLoading}
          editRouteButton={(row) =>
            `/admin/maintenance/groups/${recordIdToId(row.id)}/edit`
          }
          filterBy={
            JSON.parse(searchParams.get("filterBy") || "[]") as Filter[]
          }
          locale={{ yes: t.yes, no: t.no, actions: t.action.actions, empty: t.empty }}
          removeRouteButton={(row: MaintenancePlanGroupWithCodes) =>
            usedGroupIds.has(recordIdToId(row.id))
              ? undefined
              : `/admin/maintenance/groups/${recordIdToId(row.id)}/delete`
          }
          sortBy={searchParams.get("sortBy") || "name"}
          sortByDirection={
            searchParams.get("sortByDirection") as "asc" | "desc"
          }
          onFilter={(value: Filter[]) => {
            updateSearchParams({ filterBy: JSON.stringify(value) });
          }}
          onSort={(field, direction) => {
            updateSearchParams({
              sortBy: field,
              sortByDirection: direction,
            });
          }}
        >
          <Column field="name">{t.maintenance.groupName}</Column>
          <Column
            body={(row: MaintenancePlanGroupWithCodes) => row.codes.length}
            field="codes"
          >
            {t.maintenance.codes}
          </Column>
        </Table>
      </div>
    </>
  );
}
